import React, { FC, ReactElement } from 'react';
import DOMPurify from 'dompurify';
import { useSelector } from 'react-redux';
import Routes from '../../../../constants/routes';
import { MATERIAL_VIEWS } from '../../../../constants/search';
import useIsMobile from '../../../../hooks/use-is-mobile';
import useIsOwnAuthorPageMaterial from '../../../../hooks/use-is-own-author-page-material';
import translation from '../../../../languages';
import ClassesSequenceBlock from '../../../../common/classes-sequence-block/classes-sequence-block';
import cn from '../../../../utils/classnames';
import getLabel from '../../../../utils/material/get-label';
import previewHeightCalculator from '../../../../utils/preview/preview-height-calculator';
import MaterialLink from '../../../material-link/material-link';
import FontAwesomeIcon from '../../../../ui/font-awesome-icon/font-awesome-icon';
import Label from '../../../label/label';
import MaterialClickHandler from '../../../material-click-handler/material-click-handler';
import MaterialRating from '../../../material-rating/material-rating';
import Preview from '../../../preview/preview';
import useMaterialLabelVisibility from '../../hooks/use-material-label-visibility';
import MaterialOwner from './components/material-owner/material-owner';
import Subjects from './components/subjects/subjects';
import './material-long-template.scss';

export type MaterialLongTemplateProps = TranslationProps &
    MaterialBlock & {
        belowAllBlock?: React.JSX.Element | null;
        bottomBlock?: React.JSX.Element | null;
        classesComponent?: ReactElement;
        viewMode?: MATERIAL_VIEWS;
        descriptionHtml?: string;
        disablePreviewAnimation?: boolean;
        extraInfo?: React.JSX.Element | null;
        hideTextInfo?: boolean;
        highlightEnabled?: boolean;
        noLabel?: boolean;
        rightBlock?: React.JSX.Element | null;
        onLinkClick?: (props: { item: Material }) => void;
    };

const MARK_SANITIZE_CONFIG = { ALLOWED_TAGS: ['mark'], ALLOWED_ATTR: ['class'] };

const MaterialLongTemplate: FC<MaterialLongTemplateProps> = (props) => {
    const {
        item,
        belowAllBlock,
        bottomBlock,
        classesComponent = <ClassesSequenceBlock material={item} />,
        descriptionHtml,
        disablePreviewAnimation,
        extraInfo,
        highlightEnabled,
        mobileVersion,
        noLabel,
        rightBlock,
        onLinkClick
    } = props;

    const mobile = useIsMobile(767) || mobileVersion;
    const isOwnAuthorMaterial = useIsOwnAuthorPageMaterial(item);

    const isGridView = props.viewMode === MATERIAL_VIEWS.GRID;
    const previewSizes = previewHeightCalculator(mobile ? 'long-mobile' : isGridView ? 'long-grid' : 'long');
    const isInSchool = window.location.pathname === Routes.SCHOOL_WISHLIST.path;

    const { countryCode } = useSelector((state: RootState) => state.main.geotargeting);
    const label = !noLabel && getLabel(item, countryCode);
    const showLabel = useMaterialLabelVisibility(item, label || '');
    const handleLinkClick = onLinkClick ? (): void => onLinkClick({ item }) : undefined;

    return (
        <div
            className={cn('material-long position-relative d-flex flex-column flex-1', {
                'mobile-version': mobile,
                'with-label': showLabel
            })}
        >
            {isInSchool && (
                <div className='school-label'>
                    {KEYS_FROM_ONE_UP_TO_FOUR.map((key) => (
                        <FontAwesomeIcon key={key} width={20} height={20} name='school' />
                    ))}
                </div>
            )}
            <div className='d-flex position-relative'>
                <div className='labels-wrapper d-flex'>{label && <Label item={item} />}</div>
                <div className='preview-wrapper'>
                    <div className='position-relative'>
                        <MaterialLink
                            omitForBots
                            className='preview-cover'
                            item={item}
                            internalPath={props.internalPath}
                            title={item.title}
                            onClick={handleLinkClick}
                        >
                            <Preview
                                animation={!disablePreviewAnimation}
                                lazy
                                height={previewSizes.height}
                                width={previewSizes.width}
                                item={item}
                                materialProps={props}
                                searchRequestId={props.searchRequestId}
                            />
                        </MaterialLink>
                    </div>
                </div>
                <div className='info-action-wrapper'>
                    <div className={cn('info-action-block', { 'fixed-height': !props.hideTextInfo })}>
                        <div className='info-block flex-1 f-m'>
                            <MaterialClickHandler
                                item={item}
                                materialProps={props}
                                searchRequestId={props.searchRequestId}
                            >
                                <h3>
                                    <MaterialLink
                                        className='link-color title tsc-h4'
                                        /* @ts-ignore eslint-disable-next-line react/no-danger */
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                highlightEnabled && item.highlight?.title
                                                    ? DOMPurify.sanitize(item.highlight.title, MARK_SANITIZE_CONFIG)
                                                    : DOMPurify.sanitize(item.title)
                                        }}
                                        item={item}
                                        internalPath={props.internalPath}
                                        title={item.title}
                                        onClick={handleLinkClick}
                                    />
                                </h3>
                            </MaterialClickHandler>
                            {Boolean(item.averageRating) && (
                                <MaterialRating
                                    rating={item.averageRating as number}
                                    voices={item.totalFeedbacks}
                                    short
                                />
                            )}
                            <MaterialOwner disableLink={isOwnAuthorMaterial} item={item} />
                            {!props.hideTextInfo && (
                                <>
                                    <p
                                        className='m-desc tsc-body'
                                        /* eslint-disable-next-line react/no-danger */
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                highlightEnabled && item.highlight?.description
                                                    ? DOMPurify.sanitize(
                                                          item.highlight.description,
                                                          MARK_SANITIZE_CONFIG
                                                      )
                                                    : descriptionHtml || DOMPurify.sanitize(item.description)
                                        }}
                                    />
                                    {classesComponent}
                                    <Subjects material={item} />
                                </>
                            )}
                            {extraInfo}
                        </div>
                        <div className='right-block position-relative'>{rightBlock}</div>
                    </div>
                    <>{bottomBlock}</>
                </div>
            </div>
            {belowAllBlock}
        </div>
    );
};

const KEYS_FROM_ONE_UP_TO_FOUR = [1, 2, 3, 4] as const;

const translate = translation(['material-feedbacks', 'common']);

export default translate(MaterialLongTemplate);
